<template>
  <v-card>
    <template v-if="events != null && events.length > 0">
      <schedule-events :editable="true" :events.sync="events" :course.sync="course" />
      <v-divider />
    </template>
    <v-list three-line subheader>
      <v-subheader>Add new schedule</v-subheader>
      <v-list-item>
        <v-list-item-content>
          <v-row>
            <v-col cols="12" lg="4" sm="6" align="center">
              <v-date-picker v-model="dates" @change="rangePickerChanged" range></v-date-picker>
              <p class="mt-3">Use the calendar above to choose the date range</p>
            </v-col>
            <v-col cols="12" lg="8" sm="6">
              <v-form ref="form" v-model="valid" :lazy-validation="true">
                <v-row>
                  <v-col cols="12" lg="6">
                    <v-text-field
                      v-model="startDate"
                      label="Start Date"
                      @change="dateInputChanged"
                      prepend-icon="mdi-calendar"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" lg="6">
                    <v-text-field
                      v-model="endDate"
                      label="End Date"
                      @change="dateInputChanged"
                      prepend-icon="mdi-calendar"
                      :rules="dateRangeRules"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" lg="12">
                    <v-text-field v-model="eventTitle" label="Event Title" required />
                  </v-col>
                  <v-col cols="12" lg="12">
                    <v-textarea
                      :auto-grow="true"
                      rows="1"
                      v-model="eventDescription"
                      label="Event Description"
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12" lg="6">
                    <v-select
                      width="200px"
                      v-model="selectedDows"
                      outlined
                      multiple
                      :items="daysOfWeek"
                      label="Days of Week"
                      :rules="[ () => this.selectedDows.length > 0 || 'Day of week is required']"
                      required
                    ></v-select>
                  </v-col>
                  <v-col cols="12" lg="6">
                    <v-select
                      width="200px"
                      v-model="selectedReminders"
                      outlined
                      multiple
                      :items="reminders"
                      label="Scheduled Reminder"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" lg="3">
                    <v-menu
                      ref="menu"
                      v-model="startTimeMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      :return-value.sync="startTime24"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          readonly
                          :value="startTime"
                          label="Start Time"
                          prepend-icon="mdi-calendar-clock"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-if="startTimeMenu"
                        v-model="startTime24"
                        full-width
                        @click:minute="$refs.menu.save(startTime24)"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" lg="3">
                    <v-menu
                      ref="menu2"
                      v-model="endTimeMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      :return-value.sync="endTime24"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          readonly
                          :rules="timeRules"
                          :value="endTime"
                          label="End Time"
                          prepend-icon="mdi-calendar-clock"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-time-picker
                        v-if="endTimeMenu"
                        v-model="endTime24"
                        full-width
                        @click:minute="$refs.menu2.save(endTime24)"
                      ></v-time-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" lg="6" align="center">
                    <v-btn @click="addSchedule" :disabled="isAppLoading">{{actionBtnLabel ?  actionBtnLabel:'Add Schedule'}}</v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const ScheduleEvents = () => import("@/components/course/ScheduleEvents.vue");
import moment from "moment";
import { RRule } from 'rrule'

export default {
  props: ["course", "events", "actionBtnLabel"],
  data() {
    return {
      valid: true,
      startDate: null,
      endDate: null,
      dates: [],
      dateRangeRules: [
        () =>
          new moment(this.startDate).isBefore(new moment(this.endDate)) ||
          "End date must be after start date"
      ],
      eventTitle: "",
      eventDescription: "",
      daysOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      selectedDows: ["Monday"],
      selectedReminders: [1440, 60],
      reminders: [{text: '1 Day', value: 1440}, {text: '5 hours', value: 300}, {text: '1 hour', value: 60}],
      startTimeMenu: false,
      endTimeMenu: false,
      startTime24: "12:00",
      endTime24: "13:00",
      timeRules: [
        () =>
          this.compareTimeString(this.startTime24, this.endTime24) ||
          "End time must be after start time"
      ]
    };
  },
  components: {
    scheduleEvents: ScheduleEvents
  },
  computed: {
    ...mapGetters(["navState", "isAppLoading"]),
    startTime() {
      return moment(this.startTime24, "HH:mm").format("hh:mm a");
    },
    endTime() {
      return moment(this.endTime24, "HH:mm").format("hh:mm a");
    }
  },
  mounted() {
    this.startDate = (!this.course.scheduleStart ? moment():moment(this.course.scheduleStart)).format("YYYY-MM-DD");
    this.endDate = (!this.course.scheduleEnd ? moment():moment(this.course.scheduleEnd)).format("YYYY-MM-DD");
    this.dates = [this.startDate, this.endDate];
    this.eventTitle = this.course.name
  },
  methods: {
    ...mapActions(["hideDialog"]),
    rangePickerChanged() {
      this.startDate = this.dates[0];
      this.endDate = this.dates[1];
    },
    dateInputChanged() {
      this.dates = [this.startDate, this.endDate];
    },
    compareTimeString(time1, time2) {
      const startTime = moment(time1, "HH:mm");
      const endTime = moment(time2, "HH:mm");
      const isAfter = endTime.isAfter(startTime);
      return isAfter;
    },
    addSchedule() {
      if (!this.valid) {
        this.$store.dispatch("addMessage", {
          message: "Please check your input"
        });
        return;
      }

      const startDate = this.dates[0];
      const endDate = this.dates[1];

      if (startDate == null || endDate == null) {
        this.$store.dispatch("addMessage", {
          message: "Please choose the start and end date first",
          messageClass: "warning"
        });
        return;
      }

      const startMoment = moment(`${startDate}T${this.startTime24}`);
      const endMoment = moment(`${endDate}T${this.endTime24}`);

      const startDateTime = startMoment.format();
      const endDateTime = endMoment.format();

      const newSchedule = {
        dows: this.selectedDows,
        start: this.startTime,
        end: this.endTime,
        calendar: this.course.calendarId,
        startDateTime,
        endDateTime,
        title: this.eventTitle,
        description: this.eventDescription,
        reminders: this.selectedReminders,
        googleCourseId: this.course.googleCourseId
      };

      const rrule = this.createRRule(startMoment, endMoment, newSchedule);
      //Emit 
      this.$emit("addedSchedule", newSchedule, rrule);

      //Save event to server if course is already persisted.
      if(this.course.googleCourseId){
        this.$axios
          .post("/classroom/course/event", newSchedule)
          .then(resp => {
            const event = resp.data[0];
            const modifiedCourse = resp.data[1];
            this.events.push(event);
            this.course.scheduleStart = modifiedCourse.scheduleStart;
            this.course.scheduleEnd = modifiedCourse.scheduleEnd;
          })
          .catch(err => {
            this.$store.dispatch(
              "addErrorMessage",
              err.data.message || err.data
            );
          });
      }
    },
    createRRule(startDate, untilDate, schedule){
      const dowMap = {
        "Monday": RRule.MO,
        "Tuesday": RRule.TU,
        "Wednesday": RRule.WE,
        "Thursday": RRule.TH,
        "Friday": RRule.FR,
        "Saturday": RRule.SA,
        "Sunday": RRule.SU
      }

      const rruleDow = schedule.dows.map( dow => { return dowMap[dow]; });
      const rule = new RRule({
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: rruleDow,
        dtstart: startDate.utc().toDate(),
        until: untilDate.utc().toDate()
      })

      return rule;
    }
  }
};
</script>

<style>
</style>